/**
 * A fenced block in a V1 answer — code, JSON, YAML, a shell transcript.
 *
 * ── WHAT WENT WRONG, AND WHY THIS EXISTS ────────────────────────────────────
 *
 * V1 rendered every fence through a `components.pre` override, which REPLACES streamdown's whole
 * fenced-block pipeline rather than adding to it. Measured in the browser, on one answer carrying
 * an Apex class, a JSON result, a mermaid graph and a shell script:
 *
 *                        with the override      without it
 *   mermaid diagrams              0                 1
 *   highlighted spans             0               175
 *   copy buttons                  0                 5
 *   Monaco instances              0                 4
 *
 * So V1 was not "rendering code plainly by choice" — it had silently dropped diagram rendering,
 * every copy and download action, and all syntax highlighting, for every block in the conversation.
 *
 * ── WHAT THIS IS, AND WHAT IT IS NOT ────────────────────────────────────────
 *
 * Legacy's answer to a fence is `SkillCodeRenderer`: a full-height read-only MONACO per block, with
 * line numbers, folding and a gutter. That is right for a report and wrong for a conversation — a
 * forty-line class becomes taller than the answer around it, and four of them mount four editors.
 *
 * So this is the same CHROME with a lighter BODY:
 *   - the header is legacy's, class for class — collapse on the left, `LANGUAGE · filename ·
 *     N lines` in the middle, actions on the right — so a block reads the same in the chat as in
 *     the report and the workspace.
 *   - the actions are legacy's OWN components (`CodeBlockCopyButton`, `StreamingIndicator`).
 *   - the body is shiki, through the `@streamdown/code` plugin this pipeline already loads. Real
 *     tokens, real theme, no editor instance, no gutter, nothing to lay out.
 *   - `Open` hands the block to the tabbed editor on the right, which stays the place where code is
 *     inspected and edited properly.
 *
 * Registered as a streamdown `renderers` entry — the same extension point legacy registers its own
 * two renderers through — so the mermaid plugin, the table renderer, the link hardening and the
 * list collapsing all keep working. Nothing here is a second markdown pipeline.
 */
import { memo, useCallback, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Download, ExternalLink } from "lucide-react";
import { jsonRecordFor } from "@/services/multi-agent/jsonView";
import { JsonRecord } from "./JsonRecord";
import {
  CodeBlockCopyButton,
  StreamingIndicator,
} from "@/components/organisms/studio/SkillCodeBlockParts";
import { blockLabel, describeCodeBlock } from "@/services/multi-agent/codePreview";
import type { CodePayload } from "@/stores/weaveWorkspaceStore";
import { HighlightedLines, highlightLanguage, useIsDarkPainted, useShikiTokens } from "./codeHighlight";

/**
 * Above this the body scrolls inside its own box instead of growing the conversation.
 *
 * ~16 lines at the chat's line height. Chosen so the common case — a short class, a JSON result, a
 * command — is fully visible without a scrollbar, and a long one costs a fixed amount of the
 * answer's height instead of an unbounded amount. §8.4: the scroll box is on the content, never the
 * page.
 */
const BODY_MAX_HEIGHT = 200;

/** The chat's code line height, in px. `BODY_MAX_HEIGHT` is expressed in these. */
const LINE_HEIGHT = 20;

/**
 * Beyond this the block arrives COLLAPSED.
 *
 * A capped scroll box is still a 320px hole in the middle of an answer, and for a 500-line file the
 * reader is going to open it in the editor anyway. Same threshold legacy uses to defer its Monaco
 * (`DEFER_MONACO_AFTER_LINES`), so the two surfaces agree about what "too big to inline" means.
 */
const COLLAPSE_AFTER_LINES = 500;

/**
 * `Open` appears exactly when the body CANNOT be shown in full, and not before.
 *
 * Past this the block scrolls, so there is something the trip to the editor actually buys. Below it
 * the reader can already see everything, and an "Open" that reveals nothing is a control that lies
 * about having more. The sweep's "a SHORT shell command offers no opener" check is what pinned
 * this: a 13-line command was being offered an editor it did not need.
 */
const OPEN_AFTER_LINES = Math.floor(BODY_MAX_HEIGHT / LINE_HEIGHT);

export interface ChatCodeBlockProps {
  /** The fence body, as streamdown hands it over. */
  code: string;
  /** The fence's tag. `""` for an untagged fence. */
  language: string;
  /** The fence's info string — `filename=…` when the author put one there. */
  meta?: string;
  isIncomplete?: boolean;
}

interface Wiring {
  isStreaming: boolean;
  /** Hand the block to the tabbed editor on the right. Omitted, no `Open` is offered. */
  onOpen?: (payload: CodePayload) => void;
}

function makeChatCodeBlock({ isStreaming, onOpen }: Wiring) {
  return memo(function ChatCodeBlock({ code: raw, language, meta }: ChatCodeBlockProps) {
    // Which theme is PAINTING the page — the root `dark` class, not `resolvedTheme`. See `codeHighlight`.
    const { isDark, themeKey } = useIsDarkPainted();

    const block = useMemo(() => describeCodeBlock(language, raw), [language, raw]);
    /*
     * ── A JSON RESULT IS A RECORD, NOT SOURCE ─────────────────────────────────────────────────
     *
     * `json` lives in `CODE_LANGUAGES`, so a result object arrived through the identical card as
     * an Apex class — highlighted punctuation with a horizontal scrollbar, where the reader wanted
     * "satisfied: yes, gaps: none". `jsonRecordFor` answers non-null ONLY for a small top-level
     * object it can lay out honestly, so every other fence — including every array, every
     * unparsed block and every other language — renders exactly as it did before.
     *
     * NOT WHILE STREAMING. A half-arrived document does not parse, so this is naturally null
     * mid-stream and the block renders as code until the fence closes; that is the honest
     * behaviour and it needs no flag, but it is worth knowing it is why the view appears late.
     */
    const record = useMemo(
      () => jsonRecordFor(block.language, block.content),
      [block.language, block.content],
    );
    const codeStr = block.content;
    const fromMeta = meta?.match(/(?:filename|name)=([^\s]+)/)?.[1] ?? null;
    const filename = fromMeta ?? block.title;

    // Long blocks arrive closed; everything else arrives open. `useState` initialiser, not an
    // effect: the first paint is already the right one, so nothing collapses under the reader.
    const [collapsed, setCollapsed] = useState(block.lineCount > COLLAPSE_AFTER_LINES);
    const [copied, setCopied] = useState(false);

    // Nothing is highlighted while collapsed: the grammar fetch and the tokenising are work for
    // content nobody is looking at, and a 500-line file is exactly where that cost lands.
    const grammar = highlightLanguage(block.language);
    // `themeKey` is folded into the key so a toggle re-runs the highlight even though `isDark`
    // is read off the DOM — without it, flipping the theme would leave the old theme's tokens up.
    const tokens = useShikiTokens(
      collapsed ? "" : codeStr,
      collapsed ? null : grammar,
      isDark,
      themeKey,
    );

    const handleCopy = useCallback(async () => {
      try {
        await navigator.clipboard.writeText(codeStr);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      } catch {
        /* clipboard blocked — nothing useful to say about it */
      }
    }, [codeStr]);

    const handleDownload = useCallback(() => {
      const blob = new Blob([codeStr], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // `blockLabel` names the file when the BLOCK named it, and otherwise gives a generic label
      // with the language's extension. Nothing here invents a path in the org.
      a.download = filename ?? blockLabel(block);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, [block, codeStr, filename]);

    return (
      <div className="my-3 w-full min-w-0">
        <div className="min-w-0 overflow-hidden rounded-lg border border-border/60 bg-card">
          {/* Header — legacy's layout, class for class, so a block reads the same on every surface. */}
          <div className="flex items-center gap-2 rounded-t-lg bg-muted/40 px-2 py-1.5">
            <button
              type="button"
              title={collapsed ? "Expand" : "Collapse"}
              /*
               * `title` alone is not an accessible name the sweep will accept, and an icon-only
               * control with no name is unusable with a screen reader (§9). Names the SUBJECT too,
               * because a turn can carry several blocks and "Expand" alone says which one.
               */
              aria-label={`${collapsed ? "Expand" : "Collapse"} ${filename ?? block.language ?? "code"} block`}
              aria-expanded={!collapsed}
              onClick={() => setCollapsed((v) => !v)}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {collapsed ? (
                <ChevronRight className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
            </button>

            <div className="flex min-w-0 flex-1 items-center gap-2">
              {block.language && (
                <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {block.language}
                </span>
              )}
              {filename && (
                <span className="max-w-[180px] truncate text-xs font-medium text-foreground">
                  {filename}
                </span>
              )}
              <span className="whitespace-nowrap text-xs text-muted-foreground">
                {block.lineCount} {block.lineCount === 1 ? "line" : "lines"}
              </span>
              <StreamingIndicator active={isStreaming} />
            </div>

            <div className="flex shrink-0 flex-wrap items-center gap-1">
              {/*
                `Open` is V1's addition and the reason the chat can stay light: the right-hand
                editor is where code is inspected properly, so the chat never has to be an editor.
                Offered only for a block that does not fit — see `OPEN_AFTER_LINES`.
              */}
              {onOpen && block.lineCount > OPEN_AFTER_LINES && (
                <button
                  type="button"
                  title="Open in the editor"
                  aria-label={`Open ${filename ?? blockLabel(block)} in the editor`}
                  onClick={() =>
                    onOpen({
                      language: block.language,
                      monaco: block.monaco,
                      content: codeStr,
                      title: filename,
                      // The payload's `kind` has no `prose`; an untagged fence opens as code.
                      kind: block.kind === "prose" ? "code" : block.kind,
                    })
                  }
                  className="flex items-center gap-1 rounded px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  <span>Open</span>
                </button>
              )}
              <CodeBlockCopyButton show={true} isCopied={copied} onCopy={handleCopy} />
              <button
                type="button"
                title="Download"
                aria-label={`Download ${filename ?? blockLabel(block)}`}
                onClick={handleDownload}
                className="flex items-center gap-1 rounded px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Download className="h-3.5 w-3.5" />
                <span>Download</span>
              </button>
            </div>
          </div>

          {!collapsed && record && (
            /*
             * The record view REPLACES the body and nothing else: the header above, its collapse
             * control, `Open`, `Copy` and `Download` are untouched and still act on the raw
             * document. No second card, no second set of actions.
             *
             * It has no `maxHeight` and no scroller of its own: `jsonView` already caps how much
             * it will lay out (`PREVIEW_FIELDS`, `MAX_DEPTH`), so the height is bounded by the
             * data rather than by a box the reader has to scroll inside — which is the nested
             * second scroll region §8.4 exists to prevent.
             */
            <JsonRecord view={record} />
          )}

          {!collapsed && !record && (
            /*
             * The body: shiki tokens in a capped scroll box. `overflow-auto` on THIS element and
             * nowhere above it — a wide line scrolls the block, never the conversation (§8.4).
             */
            <pre
              /*
               * THE `pre` IS THE SCROLLER, not a wrapper around it. With the scroll box on a parent
               * and `min-w-max` here, at 375px the sweep found this element overflowing its own box
               * with `overflow-x: visible` — a line clipped rather than reachable, which is the
               * regression that check exists for. One element owns both axes, so there is no gap
               * between what overflows and what scrolls.
               */
              className="overflow-auto p-3 font-mono text-[13px] leading-5 text-foreground"
              style={{ maxHeight: BODY_MAX_HEIGHT }}
              data-testid="chat-code-body"
            >
                <HighlightedLines code={codeStr} tokens={tokens} />
            </pre>
          )}
        </div>
      </div>
    );
  });
}

/**
 * The languages this claims, and why the list is not legacy's.
 *
 * Legacy splits fences between two renderers — source in `SkillCodeRenderer`, data in
 * `SkillDataRenderer` — because each mounts a differently-configured Monaco. With a shiki body
 * there is nothing to configure differently, so one renderer covers both and there is one place a
 * block's chrome is defined.
 *
 * `mermaid` is deliberately ABSENT: the mermaid plugin claims it first and renders a diagram, which
 * is the behaviour that was lost. Claiming it here would break it again.
 */
const CHAT_LANGUAGES = [
  // Apex / Salesforce
  "apex", "apexclass", "apextrigger", "apextest", "soql", "sql", "visualforce", "aura", "lwc",
  // General source
  "java", "javascript", "typescript", "js", "ts", "jsx", "tsx", "python", "py", "ruby", "rb",
  "go", "rust", "rs", "kotlin", "swift", "c", "cpp", "csharp", "cs", "php", "html", "css", "scss",
  "graphql", "diff", "patch",
  // Shell
  "bash", "shell", "sh", "zsh", "powershell", "console", "shell-session", "terminal",
  // Data
  "json", "yaml", "yml", "xml", "csv", "tsv", "toml", "ini", "properties", "data",
  // Prose-ish fences, which still deserve a copy button and a line count
  "markdown", "md", "text", "txt", "log",
  "plaintext", "plain",
];

/*
 * ── AN UNTAGGED FENCE CANNOT BE CLAIMED, AND THAT IS THE LIBRARY'S RULE ─────
 *
 * A ``` with no info string is the shape a real run produced — 26 lines of markdown with headings
 * and a table — so this list tried to claim it with `""`. It cannot be done. Streamdown resolves a
 * renderer with:
 *
 *     renderers && e && renderers.find(n => n.language.includes(e))
 *
 * (`chunk-BO2N2NFS.js`) — the `&& e` short-circuits on a falsy language, so a fence with no tag
 * never reaches a custom renderer at all. `plaintext` and `plain` are kept because a producer that
 * writes them out IS matched.
 *
 * An untagged fence therefore renders through streamdown's own code block, which brings its own
 * copy control. What it does NOT bring is a bounded height or a scrollable body, so the sweep found
 * it clipping a long line at 375px — fixed in `.ma-answer pre` in the token CSS, where it also
 * covers any other default block. The one thing it cannot have is `Open`, which is why the header
 * exists on the claimed languages and not here.
 */

/** A streamdown `renderers` entry — the same shape `buildSkillCodeRenderer` returns. */
export function buildChatCodeRenderer(wiring: Wiring) {
  return {
    language: CHAT_LANGUAGES,
    component: makeChatCodeBlock(wiring),
  };
}
